import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import { useI18n } from "@/context/I18nContext";
import { API } from "@/context/AuthContext";
import { DASHBOARD } from "@/constants/testIds";

/**
 * Smart Products — V2 catálogo de productos inteligentes.
 *
 * Cada producto declara qué campos GP debe pedir cuando se agregue a una
 * orden. NO está conectado al flujo de órdenes todavía: este sprint es
 * estrictamente aditivo (solo CRUD del nuevo catálogo). Endpoints en
 * /api/smart-products. Las órdenes podrán consumir
 * /api/smart-products/{id}/required-fields en el futuro.
 */
const REQUIRED_FIELDS = [
  "brand",
  "model",
  "color",
  "size",
  "quantity",
  "decoration_method",
  "logo_location",
  "material",
  "width",
  "height",
  "thickness",
  "installation",
  "led",
  "comments",
];

const emptyRequired = () =>
  REQUIRED_FIELDS.reduce((acc, k) => ({ ...acc, [k]: false }), {});

const DROPDOWN_RESOURCES = [
  { state: "categories", resource: "product-categories" },
  { state: "subcategories", resource: "product-subcategories" },
  { state: "itemTypes", resource: "item-types" },
  { state: "uoms", resource: "units-of-measure" },
  { state: "brands", resource: "brands" },
  { state: "suppliers", resource: "suppliers" },
];

export default function SmartProductsPanel({ onBack }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm());
  const [saving, setSaving] = useState(false);
  const [refs, setRefs] = useState({
    categories: [],
    subcategories: [],
    itemTypes: [],
    uoms: [],
    brands: [],
    suppliers: [],
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  function initialForm() {
    return {
      name: "",
      code: "",
      description: "",
      category_id: "",
      subcategory_id: "",
      item_type_id: "",
      uom_id: "",
      default_brand_id: "",
      default_model: "",
      default_supplier_id: "",
      is_active: true,
      required_fields: emptyRequired(),
      image_url: null,
      production_time_value: "",
      production_time_unit: "hours",
    };
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/smart-products`, {
        withCredentials: true,
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRefs = useCallback(async () => {
    try {
      const results = await Promise.all(
        DROPDOWN_RESOURCES.map(({ resource }) =>
          axios
            .get(`${API}/${resource}`, { withCredentials: true })
            .then((r) => (Array.isArray(r.data) ? r.data : r.data?.items || []))
            .catch(() => []),
        ),
      );
      const next = {
        categories: [],
        subcategories: [],
        itemTypes: [],
        uoms: [],
        brands: [],
        suppliers: [],
      };
      DROPDOWN_RESOURCES.forEach(({ state }, i) => {
        next[state] = (results[i] || []).filter((x) => x.is_active !== false);
      });
      setRefs(next);
    } catch {
      // soft-fail: dropdowns will just be empty
    }
  }, []);

  useEffect(() => {
    load();
    loadRefs();
  }, [load, loadRefs]);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm());
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      code: item.code || "",
      category_id: item.category_id || "",
      subcategory_id: item.subcategory_id || "",
      item_type_id: item.item_type_id || "",
      uom_id: item.uom_id || "",
      default_brand_id: item.default_brand_id || "",
      default_model: item.default_model || "",
      default_supplier_id: item.default_supplier_id || "",
      description: item.description || "",
      is_active: !!item.is_active,
      required_fields: { ...emptyRequired(), ...(item.required_fields || {}) },
      image_url: item.image_url || null,
      production_time_value:
        item.production_time_value !== null && item.production_time_value !== undefined
          ? String(item.production_time_value)
          : "",
      production_time_unit: item.production_time_unit || "hours",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const name = (form.name || "").trim();
    if (!name) {
      toast.error(t("name_required"));
      return;
    }
    setSaving(true);
    try {
      const ptv = (form.production_time_value || "").toString().trim();
      const payload = {
        name,
        code: (form.code || "").trim() || null,
        category_id: form.category_id || null,
        subcategory_id: form.subcategory_id || null,
        item_type_id: form.item_type_id || null,
        uom_id: form.uom_id || null,
        default_brand_id: form.default_brand_id || null,
        default_model: (form.default_model || "").trim() || null,
        default_supplier_id: form.default_supplier_id || null,
        description: (form.description || "").trim() || null,
        is_active: form.is_active,
        required_fields: form.required_fields,
        image_url: form.image_url || null,
        production_time_value: ptv === "" ? null : Number(ptv),
        production_time_unit: ptv === "" ? null : form.production_time_unit,
      };
      let savedId = editing?.id;
      if (editing?.id) {
        await axios.put(`${API}/smart-products/${editing.id}`, payload, {
          withCredentials: true,
        });
      } else {
        const { data } = await axios.post(`${API}/smart-products`, payload, {
          withCredentials: true,
        });
        savedId = data.id;
      }
      // Upload deferred image (when in create flow)
      if (form._pendingImage instanceof File && savedId) {
        await uploadImageFor(savedId, form._pendingImage);
      }
      toast.success(t("saved_ok"));
      setOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const uploadImageFor = async (productId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await axios.post(
      `${API}/smart-products/${productId}/image`,
      fd,
      { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
    );
    return data;
  };

  const onPickImage = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(t("smart_products_image_hint"));
      return;
    }
    // If editing an existing product, upload immediately so the URL persists
    // even if the user cancels the modal.
    if (editing?.id) {
      try {
        setUploadingImage(true);
        const updated = await uploadImageFor(editing.id, file);
        setForm((f) => ({ ...f, image_url: updated.image_url, _pendingImage: null }));
        toast.success(t("saved_ok"));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Error");
      } finally {
        setUploadingImage(false);
      }
      return;
    }
    // Create flow: stash the file; upload after first save.
    const previewUrl = URL.createObjectURL(file);
    setForm((f) => ({ ...f, _pendingImage: file, image_url: previewUrl }));
  };

  const onRemoveImage = async () => {
    if (editing?.id && form.image_url && !form._pendingImage) {
      try {
        setUploadingImage(true);
        await axios.delete(`${API}/smart-products/${editing.id}/image`, {
          withCredentials: true,
        });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Error");
        return;
      } finally {
        setUploadingImage(false);
      }
    }
    setForm((f) => ({ ...f, image_url: null, _pendingImage: null }));
  };

  const toggle = async (item) => {
    try {
      await axios.patch(
        `${API}/smart-products/${item.id}/toggle-active`,
        null,
        { withCredentials: true },
      );
      toast.success(t("state_changed_ok"));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    return items.filter((c) => {
      if (statusFilter === "active" && !c.is_active) return false;
      if (statusFilter === "inactive" && c.is_active) return false;
      if (q) {
        const name = (c.name || "").toLowerCase();
        const code = (c.code || "").toLowerCase();
        if (!name.includes(q) && !code.includes(q)) return false;
      }
      return true;
    });
  }, [items, statusFilter, search]);

  const setField = (key, value) =>
    setForm((f) => ({ ...f, [key]: value }));

  const toggleRequired = (key) =>
    setForm((f) => ({
      ...f,
      required_fields: { ...f.required_fields, [key]: !f.required_fields[key] },
    }));

  const activeCount = (rf) =>
    Object.values(rf || {}).filter(Boolean).length;

  const lookupName = (list, id) => {
    if (!id) return null;
    return (list || []).find((x) => x.id === id)?.name || null;
  };

  const availableSubcategories = useMemo(
    () =>
      (refs.subcategories || []).filter(
        (s) => !form.category_id || s.category_id === form.category_id,
      ),
    [refs.subcategories, form.category_id],
  );

  return (
    <div data-testid={DASHBOARD.smartProductsRoot} className="space-y-6">
      <section>
        <button
          data-testid={DASHBOARD.smartProductsBackBtn}
          onClick={onBack}
          className="font-mono-tech text-[0.65rem] tracking-wider text-[var(--text-secondary)] hover:text-black mb-3"
        >
          ← {t("back")}
        </button>
        <div className="label-overline mb-1">SET / 04</div>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">
          {t("smart_products_title")}
        </h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          {t("smart_products_subtitle")}
        </p>
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="label-overline block mb-1">{t("status")}</label>
              <select
                data-testid={DASHBOARD.smartProductsFilterStatus}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-[var(--border)] px-3 py-2 font-mono-tech text-sm bg-white"
              >
                <option value="all">{t("all_statuses")}</option>
                <option value="active">{t("active_only")}</option>
                <option value="inactive">{t("inactive_only")}</option>
              </select>
            </div>
            <div>
              <label className="label-overline block mb-1">{t("search")}</label>
              <input
                data-testid={DASHBOARD.smartProductsSearch}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("smart_products_search_placeholder")}
                className="border border-[var(--border)] px-3 py-2 text-sm bg-white min-w-[220px]"
              />
            </div>
          </div>
          <button
            data-testid={DASHBOARD.smartProductsNewBtn}
            onClick={openCreate}
            className="bg-[var(--text-primary)] text-white px-5 py-2.5 font-display font-bold tracking-wide hover:bg-[var(--primary)] transition-colors"
          >
            + {t("smart_products_new_btn")}
          </button>
        </div>

        {loading ? (
          <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
            LOADING…
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid={DASHBOARD.smartProductsEmpty}
            className="border border-dashed border-[var(--border)] bg-white py-16 text-center text-[var(--text-secondary)]"
          >
            {t("smart_products_empty")}
          </div>
        ) : (
          <div
            data-testid={DASHBOARD.smartProductsList}
            className="border border-[var(--border)] bg-white divide-y divide-[var(--border)]"
          >
            {filtered.map((c) => {
              const cat = lookupName(refs.categories, c.category_id);
              const it = lookupName(refs.itemTypes, c.item_type_id);
              const count = activeCount(c.required_fields);
              return (
                <div
                  key={c.id}
                  data-testid={DASHBOARD.smartProductRow(c.id)}
                  className={`grid grid-cols-12 gap-4 items-center px-5 py-4 hover:bg-[var(--bg)] ${
                    c.is_active ? "" : "opacity-60"
                  }`}
                >
                  <div className="col-span-12 sm:col-span-5">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <div className="font-display text-lg font-bold tracking-tight">
                        {c.name}
                      </div>
                      {c.code ? (
                        <span
                          data-testid={`smart-product-code-${c.id}`}
                          className="font-mono-tech text-[0.65rem] tracking-wider bg-black text-white px-2 py-0.5"
                        >
                          {c.code}
                        </span>
                      ) : null}
                    </div>
                    <div className="font-mono-tech text-[0.65rem] tracking-wider text-[var(--text-secondary)] mt-1">
                      {[cat, it].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <span className="font-mono-tech text-[0.65rem] tracking-wider bg-[var(--bg)] border border-[var(--border)] px-2 py-1">
                      {count} {t("smart_products_required_count")}
                    </span>
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <span
                      className={`font-mono-tech text-[0.65rem] tracking-wider px-2 py-1 ${
                        c.is_active
                          ? "bg-black text-white"
                          : "border border-[var(--border)] text-[var(--text-secondary)]"
                      }`}
                    >
                      {c.is_active ? t("active") : t("inactive")}
                    </span>
                  </div>
                  <div className="col-span-12 sm:col-span-3 flex justify-end gap-2">
                    <button
                      data-testid={DASHBOARD.smartProductEditBtn(c.id)}
                      onClick={() => openEdit(c)}
                      className="border border-[var(--border)] px-3 py-1.5 text-sm font-mono-tech hover:bg-[var(--text-primary)] hover:text-white transition-colors"
                    >
                      {t("edit")}
                    </button>
                    <button
                      data-testid={DASHBOARD.smartProductToggleBtn(c.id)}
                      onClick={() => toggle(c)}
                      className="border border-[var(--border)] px-3 py-1.5 text-sm font-mono-tech hover:bg-[var(--text-primary)] hover:text-white transition-colors"
                    >
                      {c.is_active ? t("deactivate") : t("activate")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        title={
          editing?.id
            ? `${t("edit")} — ${t("smart_products_title")}`
            : t("smart_products_new_btn")
        }
        testId={DASHBOARD.smartProductFormModal}
        wide
      >
        <form onSubmit={submit} className="space-y-5">
          {/* Sección 1 — Información básica */}
          <div>
            <div className="label-overline mb-2">
              {t("smart_products_section_basic")}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="label-overline block mb-1">{t("name")}</label>
                <input
                  data-testid={DASHBOARD.smartProductFormName}
                  autoFocus
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  className="w-full border border-[var(--border)] px-3 py-2 font-sans"
                  placeholder="T-Shirt / Banner / Acrylic Sign…"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="label-overline block mb-1">
                  {t("smart_products_code")}{" "}
                  <span className="text-[var(--text-secondary)] normal-case font-sans text-[0.7rem]">
                    ({t("smart_products_code_hint")})
                  </span>
                </label>
                <input
                  data-testid={DASHBOARD.smartProductFormCode}
                  value={form.code}
                  onChange={(e) => setField("code", e.target.value.toUpperCase())}
                  className="w-full border border-[var(--border)] px-3 py-2 font-mono-tech tracking-wider uppercase"
                  placeholder="TSHIRT-001"
                  maxLength={32}
                />
              </div>

              <DropdownField
                label={t("category")}
                testId={DASHBOARD.smartProductFormCategory}
                value={form.category_id}
                onChange={(v) =>
                  setForm((f) => ({ ...f, category_id: v, subcategory_id: "" }))
                }
                options={refs.categories}
              />
              <DropdownField
                label={t("smart_products_subcategory")}
                testId={DASHBOARD.smartProductFormSubcategory}
                value={form.subcategory_id}
                onChange={(v) => setField("subcategory_id", v)}
                options={availableSubcategories}
                disabled={!form.category_id}
              />
              <DropdownField
                label={t("smart_products_item_type")}
                testId={DASHBOARD.smartProductFormItemType}
                value={form.item_type_id}
                onChange={(v) => setField("item_type_id", v)}
                options={refs.itemTypes}
              />
              <DropdownField
                label={t("smart_products_uom")}
                testId={DASHBOARD.smartProductFormUom}
                value={form.uom_id}
                onChange={(v) => setField("uom_id", v)}
                options={refs.uoms}
              />
              <DropdownField
                label={t("smart_products_default_brand")}
                testId={DASHBOARD.smartProductFormBrand}
                value={form.default_brand_id}
                onChange={(v) => setField("default_brand_id", v)}
                options={refs.brands}
              />
              <div>
                <label className="label-overline block mb-1">
                  {t("smart_products_default_model")}
                </label>
                <input
                  data-testid={DASHBOARD.smartProductFormDefaultModel}
                  value={form.default_model}
                  onChange={(e) => setField("default_model", e.target.value)}
                  className="w-full border border-[var(--border)] px-3 py-2 font-sans"
                  placeholder={t("smart_products_default_model_hint")}
                />
              </div>
              <DropdownField
                label={t("smart_products_default_supplier")}
                testId={DASHBOARD.smartProductFormSupplier}
                value={form.default_supplier_id}
                onChange={(v) => setField("default_supplier_id", v)}
                options={refs.suppliers}
              />
              <label className="flex items-center gap-2 select-none cursor-pointer self-end pb-2">
                <input
                  data-testid={DASHBOARD.smartProductFormActive}
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setField("is_active", e.target.checked)}
                />
                <span className="font-mono-tech text-sm">{t("active")}</span>
              </label>
            </div>
          </div>

          {/* Sección 2 — Imagen + Tiempo de producción */}
          <div className="border-t border-[var(--border)] pt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <div className="label-overline mb-2">
                {t("smart_products_image")}
              </div>
              <p className="text-[0.7rem] text-[var(--text-secondary)] mb-2">
                {t("smart_products_image_hint")}
              </p>
              <div className="flex items-start gap-3">
                <div
                  className="w-28 h-28 border border-[var(--border)] bg-[var(--bg)] flex items-center justify-center overflow-hidden shrink-0"
                  data-testid={DASHBOARD.smartProductFormImagePreview}
                >
                  {form.image_url ? (
                    <img
                      src={form.image_url}
                      alt="preview"
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <span className="font-mono-tech text-[0.6rem] text-[var(--text-secondary)] tracking-wider">
                      NO IMAGE
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="border border-[var(--border)] px-3 py-1.5 text-sm font-mono-tech tracking-wide bg-white hover:bg-black hover:text-white cursor-pointer transition-colors inline-block">
                    {uploadingImage
                      ? t("smart_products_image_uploading")
                      : t("smart_products_image_upload")}
                    <input
                      data-testid={DASHBOARD.smartProductFormImageInput}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      disabled={uploadingImage}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) onPickImage(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {form.image_url ? (
                    <button
                      type="button"
                      data-testid={DASHBOARD.smartProductFormImageRemove}
                      onClick={onRemoveImage}
                      disabled={uploadingImage}
                      className="border border-[var(--border)] px-3 py-1.5 text-sm font-mono-tech tracking-wide hover:bg-black hover:text-white transition-colors disabled:opacity-60"
                    >
                      {t("smart_products_image_remove")}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            <div>
              <div className="label-overline mb-2">
                {t("smart_products_production_time")}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  data-testid={DASHBOARD.smartProductFormProdTimeValue}
                  type="number"
                  min="0"
                  step="any"
                  value={form.production_time_value}
                  onChange={(e) =>
                    setField("production_time_value", e.target.value)
                  }
                  placeholder="0"
                  className="border border-[var(--border)] px-3 py-2 font-mono-tech"
                />
                <select
                  data-testid={DASHBOARD.smartProductFormProdTimeUnit}
                  value={form.production_time_unit}
                  onChange={(e) =>
                    setField("production_time_unit", e.target.value)
                  }
                  className="border border-[var(--border)] px-3 py-2 font-mono-tech bg-white"
                >
                  <option value="minutes">
                    {t("smart_products_production_time_unit_minutes")}
                  </option>
                  <option value="hours">
                    {t("smart_products_production_time_unit_hours")}
                  </option>
                  <option value="days">
                    {t("smart_products_production_time_unit_days")}
                  </option>
                </select>
              </div>
              <div className="mt-4">
                <label className="label-overline block mb-1">
                  {t("smart_products_description")}
                </label>
                <textarea
                  data-testid={DASHBOARD.smartProductFormDescription}
                  rows={2}
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  className="w-full border border-[var(--border)] px-3 py-2 font-sans text-sm"
                  placeholder={t("smart_products_description_hint")}
                />
              </div>
            </div>
          </div>

          {/* Sección 3 — Campos requeridos */}
          <div className="border-t border-[var(--border)] pt-4">
            <div className="label-overline mb-1">
              {t("smart_products_section_fields")}
            </div>
            <p className="text-xs text-[var(--text-secondary)] mb-2">
              {t("smart_products_section_fields_hint")}
            </p>
            <div
              data-testid="smart-product-orders-ready-note"
              className="mb-3 border-l-2 border-emerald-500 bg-emerald-50 px-3 py-2 text-[0.7rem] text-emerald-900 font-mono-tech leading-snug"
            >
              {t("smart_products_orders_ready_note")}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {REQUIRED_FIELDS.map((k) => (
                <label
                  key={k}
                  className={`flex items-center gap-2 border px-3 py-2 cursor-pointer select-none transition-colors ${
                    form.required_fields[k]
                      ? "border-black bg-black text-white"
                      : "border-[var(--border)] bg-white"
                  }`}
                >
                  <input
                    data-testid={DASHBOARD.smartProductFieldToggle(k)}
                    type="checkbox"
                    checked={!!form.required_fields[k]}
                    onChange={() => toggleRequired(k)}
                    className="accent-black"
                  />
                  <span className="text-sm font-mono-tech tracking-wide">
                    {t(`smart_products_field_${k}`)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
            <button
              type="button"
              data-testid={DASHBOARD.smartProductFormCancel}
              onClick={() => {
                setOpen(false);
                setEditing(null);
              }}
              className="border border-[var(--border)] px-4 py-2 font-mono-tech text-sm"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              data-testid={DASHBOARD.smartProductFormSubmit}
              disabled={saving}
              className="bg-[var(--text-primary)] text-white px-5 py-2 font-display font-bold tracking-wide disabled:opacity-60"
            >
              {saving ? "…" : t("save")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function DropdownField({ label, testId, value, onChange, options, disabled }) {
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <select
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full border border-[var(--border)] px-3 py-2 font-sans bg-white disabled:bg-[var(--bg)] disabled:cursor-not-allowed"
      >
        <option value="">—</option>
        {(options || []).map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
}
